import { useMemo } from 'react';
import { LineSegment2 } from '../domain/geometry/geometric-types';
import { SheetShapeDeepFragment } from '../gql/graphql';
import { findClosestParallelEdgeToSegment } from '../components/orderContractor/pdf-shapes/protan/edge-detection';
import {
  EdgeMatchResult,
  KileTypeSKU,
} from '../components/orderContractor/pdf-shapes/protan/wedge-types';
import { selectWedgeType } from '../components/orderContractor/pdf-shapes/protan/wedge-calculations';

export type Diamond = {
  triangle1: boolean;
  triangle2: boolean;
  triangle3: boolean;
  triangle4: boolean;
};

export function useDiamondRendering(
  segments: LineSegment2[],
  renderedSheetShapes: Record<string, SheetShapeDeepFragment[]>,
  unitScale: number | null
) {
  // Generate base diamonds for each segment
  const baseDiamonds = useMemo(() => {
    return segments.map((_, index) => {
      // Create diamonds based on segment position
      if (index === 0) {
        // First segment: triangle1 and triangle3 should be false (only second triangles visible)
        return {
          triangle1: false,
          triangle2: true,
          triangle3: false,
          triangle4: true,
        };
      } else if (index === segments.length - 1) {
        // Last segment: triangle2 and triangle4 should be false (only first triangles visible)
        return {
          triangle1: true,
          triangle2: false,
          triangle3: true,
          triangle4: false,
        };
      } else {
        // Middle segments: all triangles visible
        return {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        };
      }
    });
  }, [segments]);

  // Find closest parallel edges for each segment
  const closestEdgesResults = useMemo(() => {
    if (!renderedSheetShapes || !unitScale) return [];

    return segments.map((segment) => {
      return findClosestParallelEdgeToSegment(
        segment,
        renderedSheetShapes,
        0.1,
        unitScale
      );
    });
  }, [segments, renderedSheetShapes, unitScale]);

  // Adjust diamonds based on edge detection
  const adjustedDiamonds = useMemo(() => {
    if (!unitScale) return baseDiamonds;

    return baseDiamonds.map((diamond, index) => {
      const edgeResult = closestEdgesResults[index];

      // If no closest edge found, return the base diamond
      if (!edgeResult) return diamond;

      // Create a copy of the diamond to modify
      const adjustedDiamond = { ...diamond };

      // Only modify diamonds for edges closer than 1m
      if (edgeResult.isCloserThan100mm) {
        if (index === 0) {
          // First segment: only modify triangle2 and triangle4 based on edge position
          if (edgeResult.isOnLeftSide) {
            // Do nothing to triangle1 (already false)
            adjustedDiamond.triangle2 = false;
            // Do nothing to triangle3 (already false)
            // triangle4 remains true
          } else {
            // Do nothing to triangle1 (already false)
            // triangle2 remains true
            // Do nothing to triangle3 (already false)
            adjustedDiamond.triangle4 = false;
          }
        } else if (index === segments.length - 1) {
          // Last segment: only modify triangle1 and triangle3 based on edge position
          if (edgeResult.isOnLeftSide) {
            adjustedDiamond.triangle1 = false;
            // Do nothing to triangle2 (already false)
            // triangle3 remains true
            // Do nothing to triangle4 (already false)
          } else {
            // triangle1 remains true
            // Do nothing to triangle2 (already false)
            adjustedDiamond.triangle3 = false;
            // Do nothing to triangle4 (already false)
          }
        } else {
          // Middle segments: modify all triangles based on edge position
          if (edgeResult.isOnLeftSide) {
            adjustedDiamond.triangle1 = false;
            adjustedDiamond.triangle2 = false;
          } else {
            adjustedDiamond.triangle3 = false;
            adjustedDiamond.triangle4 = false;
          }
        }
      }

      return adjustedDiamond;
    });
  }, [baseDiamonds, closestEdgesResults, segments, unitScale]);

  // Calculate kile types for each segment based on diamond configuration and segment length
  const kileTypes = useMemo(() => {
    if (!unitScale || segments.length === 0) return [];

    return segments.map((segment, index) => {
      const diamond = adjustedDiamonds[index];

      // Calculate segment length in meters
      const segmentLengthPdf = Math.sqrt(
        Math.pow(segment[1][0] - segment[0][0], 2) +
          Math.pow(segment[1][1] - segment[0][1], 2)
      );
      const segmentLengthMeters = segmentLengthPdf * unitScale;

      // Round to 2 decimal places to avoid floating point issues
      const roundedLength = Math.round(segmentLengthMeters * 100) / 100;

      // Get kile type using the wedge calculation utility
      const kileType = selectWedgeType(diamond, roundedLength);

      // Default to a common type if no valid type returned
      return kileType || ('RW257379' as KileTypeSKU);
    });
  }, [segments, adjustedDiamonds, unitScale]);

  // Calculate midpoints for each segment for label positioning
  const segmentMidpoints = useMemo(() => {
    return segments.map((segment) => {
      return [
        (segment[0][0] + segment[1][0]) / 2,
        (segment[0][1] + segment[1][1]) / 2,
      ] as [number, number];
    });
  }, [segments]);

  // Extract just the edges from the results for visualization
  const closestEdges = useMemo(() => {
    return closestEdgesResults
      .filter(
        (result): result is NonNullable<EdgeMatchResult> => result !== null
      )
      .map((result) => result.edge);
  }, [closestEdgesResults]);

  return {
    diamonds: adjustedDiamonds,
    closestEdges,
    kileTypes,
    segmentMidpoints,
    KileTypeSKU,
  };
}
